import React, {useEffect, useState} from 'react';
import axios from 'axios';
import Form from 'react-bootstrap/Form';
import AsyncSelect from 'react-select/async';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import ActionAssignBaseFormFields from './ActionAssignBaseFormFields';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {isFieldRestricted} from '@/utils/fieldRestrictions';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

interface Team {
    id: number;
    name: string;
}

interface TeamOption {
    label: string;
    value: number;
}

interface TeamsResponse {
    data: {
        items: Team[];
    };
}

interface FormikValues {
    params: {
        teamId: number;
    };
}

interface ActionAssignTeamFormFieldsProps {
    formik: any;
    setTimeoutValidity: () => void;
}

const loadTeamsRequest = (inputValue: string): Promise<TeamsResponse> => axios.get(window.URLS.teamsResource, {
    params: {
        query: inputValue,
    },
})
    .catch((error) => {
        if (error.response.status === 400) {
            return error.response;
        }

        throw error;
    });

const loadTeamsRequestDebounced = AwesomeDebouncePromise(loadTeamsRequest, 280);

const ActionAssignTeamFormFields: React.FC<ActionAssignTeamFormFieldsProps> = React.memo((
    {
        formik,
        setTimeoutValidity,
    },
) => {
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const teamRestricted = isFieldRestricted('team_pii', restrictedFieldKeys);

    const getTeamOptions = async (inputValue: string): Promise<TeamOption[]> => {
        const results = await loadTeamsRequestDebounced(inputValue);
        return results.data.items.map((team) => ({
            label: team.name || `Team #${team.id}`,
            value: team.id,
        }));
    };

    const [teamName, setTeamName] = useState<string>('');

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            const response = await axios.get<Team>(`${window.URLS.teamsResource}/${formik.values.params.teamId}`);
            const team = response.data;

            setTeamName(team.name || `Team #${formik.values.params.teamId}`);
        };

        if (formik.values.params.teamId) {
            fetchData();
        }
    }, [formik.values.params.teamId]);

    const setSelectValue = (newValue: TeamOption | null): void => {
        if (newValue) {
            formik.setFieldValue('params.teamId', newValue.value);
            setTeamName(newValue.label);
        }
    };

    if (teamRestricted) {
        return (
            <>
                <Form.Group controlId="params.teamId">
                    <Form.Label>{i18n.t('jt_select_the_team_that_will_be_assigned.text')}</Form.Label>
                    <RestrictedFieldInput />
                </Form.Group>
                <ActionAssignBaseFormFields formik={formik} setTimeoutValidity={setTimeoutValidity} />
            </>
        );
    }

    return (
        <>
            <Form.Group controlId="params.teamId">
                <Form.Label> {i18n.t('jt_select_the_team_that_will_be_assigned.text')}</Form.Label>
                <AsyncSelect<TeamOption>
                    isClearable={false}
                    cacheOptions
                    defaultOptions
                    value={{
                        label: teamName,
                        value: formik.values.params.teamId,
                    }}
                    loadOptions={getTeamOptions}
                    hideSelectedOptions={false}
                    onChange={setSelectValue}
                    onBlur={formik.handleBlur}
                    styles={{
                        menu: (provided) => ({
                            ...provided,
                            zIndex: 10,
                        }),
                        control: (provided) => {
                            if (formik.touched.params?.teamId && formik.errors.params?.teamId) {
                                return {
                                    ...provided,
                                    border: '1px solid #dc3545',
                                };
                            }

                            return provided;
                        },
                    }}
                    aria-label="select-team-to-assign"
                />
                <Form.Control.Feedback
                    type="invalid"
                    style={{display: formik.touched.params?.teamId && formik.errors.params?.teamId ? 'block' : 'none'}}
                >
                    {formik.errors.params?.teamId}
                </Form.Control.Feedback>
            </Form.Group>

            <ActionAssignBaseFormFields formik={formik} setTimeoutValidity={setTimeoutValidity}/>
        </>
    );
});

ActionAssignTeamFormFields.displayName = 'ActionAssignTeamFormFields';

export default ActionAssignTeamFormFields; 